/**
 * Types d'exécution du simulateur : vecteurs immuables, état de simulation, entrées.
 * Aucune dépendance, aucun accès DOM, aucune source de non-déterminisme.
 *
 * Fidélité C#/Unity : les `float` sont arrondis en float32 (`Math.fround`) à chaque opération,
 * comme dans Unity — `Debug.Log(0.1f + 0.2f)` affiche donc la même chose que dans l'éditeur.
 */

import type { KeyCode } from './whitelist';
export type { KeyCode } from './whitelist';

export const f32 = Math.fround;

export class Vec3 {
  readonly x: number;
  readonly y: number;
  readonly z: number;

  constructor(x: number, y: number, z = 0) {
    this.x = f32(x);
    this.y = f32(y);
    this.z = f32(z);
  }

  static readonly zero = new Vec3(0, 0, 0);
  static readonly one = new Vec3(1, 1, 1);
  static readonly up = new Vec3(0, 1, 0);
  static readonly down = new Vec3(0, -1, 0);
  static readonly left = new Vec3(-1, 0, 0);
  static readonly right = new Vec3(1, 0, 0);
  static readonly forward = new Vec3(0, 0, 1);
  static readonly back = new Vec3(0, 0, -1);

  add(o: Vec3): Vec3 { return new Vec3(this.x + o.x, this.y + o.y, this.z + o.z); }
  sub(o: Vec3): Vec3 { return new Vec3(this.x - o.x, this.y - o.y, this.z - o.z); }
  scale(k: number): Vec3 { return new Vec3(this.x * k, this.y * k, this.z * k); }
  div(k: number): Vec3 { return new Vec3(this.x / k, this.y / k, this.z / k); }
  neg(): Vec3 { return new Vec3(-this.x, -this.y, -this.z); }
  get sqrMagnitude(): number { return f32(this.x * this.x + this.y * this.y + this.z * this.z); }
  get magnitude(): number { return f32(Math.sqrt(this.x * this.x + this.y * this.y + this.z * this.z)); }
  /** Comme Unity : un vecteur (quasi) nul se normalise en `zero`, jamais en NaN. */
  get normalized(): Vec3 {
    const m = this.magnitude;
    return m > 1e-5 ? this.div(m) : Vec3.zero;
  }
  equals(o: Vec3): boolean { return this.x === o.x && this.y === o.y && this.z === o.z; }
  with(component: 'x' | 'y' | 'z', value: number): Vec3 {
    return new Vec3(component === 'x' ? value : this.x, component === 'y' ? value : this.y, component === 'z' ? value : this.z);
  }
  toVec2(): Vec2 { return new Vec2(this.x, this.y); }
  static distance(a: Vec3, b: Vec3): number { return a.sub(b).magnitude; }
  static dot(a: Vec3, b: Vec3): number { return f32(a.x * b.x + a.y * b.y + a.z * b.z); }
  static clampMagnitude(v: Vec3, max: number): Vec3 {
    const sq = v.sqrMagnitude;
    return sq > max * max ? v.normalized.scale(max) : v;
  }
  /** Format Unity (`Vector3.ToString()` : 2 décimales). */
  toString(): string { return `(${fmtF2(this.x)}, ${fmtF2(this.y)}, ${fmtF2(this.z)})`; }
}

export class Vec2 {
  readonly x: number;
  readonly y: number;

  constructor(x: number, y: number) {
    this.x = f32(x);
    this.y = f32(y);
  }

  static readonly zero = new Vec2(0, 0);
  static readonly one = new Vec2(1, 1);
  static readonly up = new Vec2(0, 1);
  static readonly down = new Vec2(0, -1);
  static readonly left = new Vec2(-1, 0);
  static readonly right = new Vec2(1, 0);

  add(o: Vec2): Vec2 { return new Vec2(this.x + o.x, this.y + o.y); }
  sub(o: Vec2): Vec2 { return new Vec2(this.x - o.x, this.y - o.y); }
  scale(k: number): Vec2 { return new Vec2(this.x * k, this.y * k); }
  div(k: number): Vec2 { return new Vec2(this.x / k, this.y / k); }
  neg(): Vec2 { return new Vec2(-this.x, -this.y); }
  get sqrMagnitude(): number { return f32(this.x * this.x + this.y * this.y); }
  get magnitude(): number { return f32(Math.sqrt(this.x * this.x + this.y * this.y)); }
  get normalized(): Vec2 {
    const m = this.magnitude;
    return m > 1e-5 ? this.div(m) : Vec2.zero;
  }
  equals(o: Vec2): boolean { return this.x === o.x && this.y === o.y; }
  with(component: 'x' | 'y', value: number): Vec2 {
    return new Vec2(component === 'x' ? value : this.x, component === 'y' ? value : this.y);
  }
  toVec3(): Vec3 { return new Vec3(this.x, this.y, 0); }
  static distance(a: Vec2, b: Vec2): number { return a.sub(b).magnitude; }
  static dot(a: Vec2, b: Vec2): number { return f32(a.x * b.x + a.y * b.y); }
  static clampMagnitude(v: Vec2, max: number): Vec2 {
    const sq = v.sqrMagnitude;
    return sq > max * max ? v.normalized.scale(max) : v;
  }
  toString(): string { return `(${fmtF2(this.x)}, ${fmtF2(this.y)})`; }
}

/** Valeur manipulée par l'interpréteur. Les `KeyCode` sont représentés par leur nom. */
export type Value = number | boolean | string | Vec2 | Vec3;

/**
 * Entrées d'une frame. `horizontal`/`vertical` alimentent `Input.GetAxis` ET `Input.GetAxisRaw`
 * (valeurs -1/0/1, SANS le lissage qu'Unity applique à `GetAxis` — documenté dans le README).
 * `keys` = touches maintenues (`GetKey`), `keysDown` = touches pressées cette frame seulement
 * (`GetKeyDown`), `keysUp` = touches relâchées cette frame (`GetKeyUp`).
 */
export interface SimInput {
  horizontal: -1 | 0 | 1;
  vertical: -1 | 0 | 1;
  keys: ReadonlySet<KeyCode>;
  keysDown: ReadonlySet<KeyCode>;
  keysUp?: ReadonlySet<KeyCode>;
}

const NO_KEYS: ReadonlySet<KeyCode> = new Set();

export const EMPTY_INPUT: SimInput = Object.freeze({ horizontal: 0, vertical: 0, keys: NO_KEYS, keysDown: NO_KEYS, keysUp: NO_KEYS });

/** Construit une entrée à partir d'un objet partiel (pratique pour les tests et l'UI). */
export function makeInput(partial: Partial<SimInput> = {}): SimInput {
  return {
    horizontal: partial.horizontal ?? 0,
    vertical: partial.vertical ?? 0,
    keys: partial.keys ?? NO_KEYS,
    keysDown: partial.keysDown ?? NO_KEYS,
    keysUp: partial.keysUp ?? NO_KEYS,
  };
}

/**
 * État observable de la simulation.
 * `position` : position courante (Vec3 immuable, remplacé à chaque modification).
 * `elapsed`  : secondes simulées écoulées (= frame / fps).
 * `frame`    : nombre d'appels à `Update()` effectués.
 * `logs`     : sorties `Debug.Log`, plafonnées à `MAX_LOG_LINES`.
 */
export interface SimState {
  position: Vec3;
  elapsed: number;
  frame: number;
  logs: string[];
}

export const MAX_LOG_LINES = 200;

/** Cadences simulées. */
export type SimFps = 5 | 30 | 60;
export const SIM_FPS_VALUES: readonly SimFps[] = [5, 30, 60];

/** Arène du mini-jeu (unités Unity, 1 u = 1 m) : plan XY, z ignoré mais conservé. L'interpréteur ne clampe PAS. */
export const ARENA = Object.freeze({ halfWidth: 8, halfHeight: 4.5 });

// --- Formatage à la C# (Debug.Log) ---

function fmtF2(v: number): string {
  return (Object.is(v, -0) ? 0 : v).toFixed(2);
}

/** `float.ToString()` façon Unity/Mono : 7 chiffres significatifs, sans zéros inutiles. */
export function formatFloat(v: number): string {
  if (!Number.isFinite(v)) return Number.isNaN(v) ? 'NaN' : v > 0 ? 'Infinity' : '-Infinity';
  if (Object.is(v, -0)) return '0';
  const s = Number(v.toPrecision(7)).toString();
  return s.includes('e') ? s.replace('e+', 'E+').replace('e-', 'E-') : s;
}

export function formatValue(v: Value, type?: string): string {
  if (typeof v === 'boolean') return v ? 'True' : 'False';
  if (typeof v === 'string') return v;
  if (v instanceof Vec3 || v instanceof Vec2) return v.toString();
  if (type === 'int') return String(Math.trunc(v));
  return formatFloat(v);
}
